import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../../../serialization/enum-utils.js';
import { MapUtils } from '../../../../utility/map-utils.js';
import { VersionRange } from '../../../core/version-range.js';
import {
  cloneData, cloneDataOrNull, cloneListOfCloneable, DataModel, DataModelBuilder, isSameData,
  isSameList, SerializationContext, SerializedData,
} from '../../../dto/dto.js';
import { OnlineTransactionDetails } from '../../online-transaction-details.js';
import { GenericOrderEntry, GenericOrderEntryStatus } from '../generic-order-entry.js';
import { OrderSideEffect } from '../order-side-effect.js';
import { EventBookingStrategy } from '../../event-booking-protocol/event-booking-strategy.js';
import { BusinessModuleRef } from '../../business-module-ref.js';
import { ProductOrderItem } from '../../product-order-item.js';
import { Price } from '../../price.js';
import { InvoiceVat } from '../../invoicing/invoice-vat.js';
import { JSONUtils } from '../../../../utility/json-utils.js';
import { TimeZone } from '../../../../time/timezone.js';
import { Dayjs } from 'dayjs';
import { ElectronicDeliveryMethod } from '../../electronic-delivery-method.js';
import { Refund } from '../../discounts/refund.js';
import { OfflineTransactionDetails } from '../../offline-transaction-details.js';

export class GenericOrderProductEntry extends GenericOrderEntry {
  static readonly TYPE_ID: string = 'eab31f27736d4460b9b615a9cd95a76b';
  static readonly TYPE_NAME: string = 'Products';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly uniqueId: string,
    public readonly orderId: string,
    public readonly entryGroupId: string | null,
    public readonly note: string | null,
    public readonly status: GenericOrderEntryStatus,
    public readonly creatorId: string,
    public readonly creationDate: Dayjs,
    public readonly eventId: string,
    public readonly bookingStrategy: EventBookingStrategy,
    public readonly businessRef: BusinessModuleRef,
    public readonly products: ProductOrderItem[],
    public readonly onlineTransactionDetails: OnlineTransactionDetails | null,
    public readonly offlineTransactionDetails: OfflineTransactionDetails | null,
    public readonly sideEffects: OrderSideEffect[],
    public readonly cancelledDate: Dayjs | null,
    public readonly cancelledBy: string | null,
    public readonly deliveryMethod: ElectronicDeliveryMethod,
    public readonly refunds: Refund[],
    public applicationFee: Price | null,
    public applicationFeeVat: InvoiceVat | null,
  ) {
    super();
  }

  get $typeId(): string {
    return GenericOrderProductEntry.TYPE_ID;
  }

  get $typeName(): string {
    return GenericOrderProductEntry.TYPE_NAME;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, GenericOrderProductEntry.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    orderId: string,
    entryGroupId: string | null,
    note: string | null,
    status: GenericOrderEntryStatus,
    creatorId: string,
    creationDate: Dayjs,
    eventId: string,
    bookingStrategy: EventBookingStrategy,
    businessRef: BusinessModuleRef,
    products: ProductOrderItem[],
    onlineTransactionDetails: OnlineTransactionDetails | null,
    offlineTransactionDetails: OfflineTransactionDetails | null,
    sideEffects: OrderSideEffect[],
    cancelledDate: Dayjs | null,
    cancelledBy: string | null,
    applicationFee: Price | null,
    applicationFeeVat: InvoiceVat | null,
    deliveryMethod: ElectronicDeliveryMethod,
    refunds?: Refund[],
  }): GenericOrderProductEntry {
    return new GenericOrderProductEntry(
        args.uniqueId,
        args.orderId,
        args.entryGroupId,
        args.note,
        args.status,
        args.creatorId,
        args.creationDate,
        args.eventId,
        args.bookingStrategy,
        args.businessRef,
        args.products,
        args.onlineTransactionDetails,
        args.offlineTransactionDetails,
        args.sideEffects,
        args.cancelledDate,
        args.cancelledBy,
        args.deliveryMethod,
        args.refunds ?? [],
        args.applicationFee,
        args.applicationFeeVat,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GenericOrderProductEntry {
    return GenericOrderProductEntry.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      orderId: MapUtils.getString(map, 'orderId'),
      entryGroupId: MapUtils.getStringOrNull(map, 'entryGroupId'),
      note: MapUtils.getStringOrNull(map, 'note'),
      status: toEnumFromString(MapUtils.getString(map, '$status'), GenericOrderEntryStatus),
      creatorId: MapUtils.getString(map, 'creatorId'),
      creationDate: context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
      eventId: MapUtils.getString(map, 'eventId'),
      bookingStrategy: toEnumFromString(MapUtils.getString(map, 'bookingStrategy'), EventBookingStrategy),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      products: MapUtils.getList<ProductOrderItem>(map, 'products', (value) => context.fromData<ProductOrderItem>(JSONUtils.decodeRecord(value))),
      onlineTransactionDetails: context.fromDataOrNull<OnlineTransactionDetails>(MapUtils.getRecordOrNull(map, 'onlineTransactionDetails')),
      offlineTransactionDetails: context.fromDataOrNull<OfflineTransactionDetails>(MapUtils.getRecordOrNull(map, 'offlineTransactionDetails')),
      sideEffects: MapUtils.getList<OrderSideEffect>(map, 'sideEffects', (value) => context.fromData<OrderSideEffect>(JSONUtils.decodeRecord(value))),
      cancelledDate: context.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'cancelledDate')),
      cancelledBy: MapUtils.getStringOrNull(map, 'cancelledBy'),
      applicationFee: Price.decodeOrNull(MapUtils.getStringOrNull(map, 'applicationFee')),
      applicationFeeVat: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'applicationFeeVat'), InvoiceVat),
      deliveryMethod: toEnumFromString(MapUtils.getString(map, 'deliveryMethod'), ElectronicDeliveryMethod),
      refunds: MapUtils.getList<Refund>(map, 'refunds', (value) => {
        return context.fromData<Refund>(value as SerializedData);
      }),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'orderId': this.orderId,
      'entryGroupId': this.entryGroupId,
      'note': this.note,
      '$status': fromEnumAsString(this.status),
      'creatorId': this.creatorId,
      'creationDate': context.encodeDate(this.creationDate),
      'eventId': this.eventId,
      'bookingStrategy': fromEnumAsString(this.bookingStrategy),
      'businessRef': this.businessRef.encode(),
      'products': this.products.map((e) => context.toData(e)),
      'onlineTransactionDetails': context.toDataOrNull(this.onlineTransactionDetails),
      'offlineTransactionDetails': context.toDataOrNull(this.offlineTransactionDetails),
      'sideEffects': this.sideEffects.map((e) => context.toData(e)),
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
      'applicationFee': this.applicationFee?.encode() ?? null,
      'applicationFeeVat': fromEnumAsStringOrNull(this.applicationFeeVat),
      'deliveryMethod': fromEnumAsString(this.deliveryMethod),
      'refunds': this.refunds.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return this.copyWith() as this;
  }

  copyWith({
    uniqueId = this.uniqueId,
    orderId = this.orderId,
    entryGroupId = this.entryGroupId,
    note = this.note,
    status = this.status,
    creatorId = this.creatorId,
    creationDate = TimeZone.cloneDate(this.creationDate),
    eventId = this.eventId,
    bookingStrategy = this.bookingStrategy,
    businessRef = cloneData(this.businessRef),
    products = cloneListOfCloneable(this.products),
    onlineTransactionDetails = cloneDataOrNull(this.onlineTransactionDetails),
    offlineTransactionDetails = cloneDataOrNull(this.offlineTransactionDetails),
    sideEffects = cloneListOfCloneable(this.sideEffects),
    cancelledDate = TimeZone.cloneDateOrNull(this.cancelledDate),
    cancelledBy = this.cancelledBy,
    applicationFee = cloneDataOrNull(this.applicationFee),
    applicationFeeVat = this.applicationFeeVat,
    deliveryMethod = this.deliveryMethod,
    refunds = cloneListOfCloneable(this.refunds),
  }: {
    uniqueId?: string,
    orderId?: string,
    entryGroupId?: string | null,
    note?: string | null,
    status?: GenericOrderEntryStatus,
    creatorId?: string,
    creationDate?: Dayjs,
    eventId?: string,
    bookingStrategy?: EventBookingStrategy,
    businessRef?: BusinessModuleRef,
    products?: ProductOrderItem[],
    onlineTransactionDetails?: OnlineTransactionDetails | null,
    offlineTransactionDetails?: OfflineTransactionDetails | null,
    sideEffects?: OrderSideEffect[],
    cancelledDate?: Dayjs | null,
    cancelledBy?: string | null,
    applicationFee?: Price | null,
    applicationFeeVat?: InvoiceVat | null,
    deliveryMethod?: ElectronicDeliveryMethod,
    refunds?: Refund[],
  } = {}): this {
    return GenericOrderProductEntry.create({
      uniqueId,
      orderId,
      entryGroupId,
      note,
      status,
      creatorId,
      creationDate,
      eventId,
      bookingStrategy,
      businessRef,
      products,
      onlineTransactionDetails,
      offlineTransactionDetails,
      sideEffects,
      cancelledDate,
      cancelledBy,
      applicationFee,
      applicationFeeVat,
      deliveryMethod,
      refunds,
    }) as this;
  }

  copyAsAborted(): this {
    return this.copyWith({
      status: GenericOrderEntryStatus.Aborted,
    });
  }

  copyAsCanceled(cancelledBy: string | null, cancelledDate: Dayjs, isRefund: boolean): this {
    return this.copyWith({
      status: isRefund ? GenericOrderEntryStatus.Refunded : GenericOrderEntryStatus.Canceled,
      cancelledDate: TimeZone.cloneDateOrNull(this.cancelledDate) ?? cancelledDate,
      cancelledBy: this.cancelledBy ?? cancelledBy,
    });
  }

  copyWithOnlineTransactionDetails(transactionDetails: OnlineTransactionDetails): this {
    return this.copyWith({
      onlineTransactionDetails: transactionDetails,
    });
  }

  copyWithOfflineTransactionDetails(transactionDetails: OfflineTransactionDetails): this {
    return this.copyWith({
      offlineTransactionDetails: transactionDetails,
    });
  }

  isSame(other: GenericOrderProductEntry): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.onlineTransactionDetails, this.onlineTransactionDetails) &&
      isSameData(other.offlineTransactionDetails, this.offlineTransactionDetails) &&
      isSameList(other.products, this.products, isSameData) &&
      isSameList(other.sideEffects, this.sideEffects, isSameData) &&
      other.uniqueId === this.uniqueId &&
      other.orderId === this.orderId &&
      other.entryGroupId === this.entryGroupId &&
      other.note === this.note &&
      other.creatorId === this.creatorId &&
      other.eventId === this.eventId &&
      other.bookingStrategy === this.bookingStrategy &&
      other.cancelledDate === this.cancelledDate &&
      other.cancelledBy === this.cancelledBy &&
      isSameData(other.applicationFee, this.applicationFee) &&
      isSameData(other.businessRef, this.businessRef) &&
      other.creationDate === this.creationDate &&
      other.status === this.status &&
      other.applicationFeeVat == this.applicationFeeVat &&
      other.deliveryMethod === this.deliveryMethod &&
      isSameList(other.refunds, this.refunds, isSameData);
  }

  tryGetApplicationFee(): Price | null {
    return this.applicationFee;
  }

  tryGetApplicationFeeVat(): InvoiceVat | null {
    return this.applicationFeeVat;
  }

  tryGetRefunds(): Refund[] | null {
    return this.refunds;
  }

  tryAddRefund(refund: Refund): boolean {
    this.refunds.push(refund);
    return true;
  }

  tryGetEventId(): string | null {
    return this.eventId;
  }

  static verifyType(entry: DataModel): boolean {
    return entry instanceof GenericOrderProductEntry;
  }

  static assertType(entry: DataModel): entry is GenericOrderProductEntry {
    return entry instanceof GenericOrderProductEntry;
  }
}

export class GenericOrderProductEntryBuilder extends DataModelBuilder<GenericOrderProductEntry> {
  build(context: SerializationContext, map: SerializedData): GenericOrderProductEntry {
    return GenericOrderProductEntry.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, '$status', GenericOrderEntryStatus, GenericOrderEntryStatus.Open);

    if(versionRange.contains(GenericOrderProductEntry.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(GenericOrderProductEntry.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In version 2 we added `offlineTransactionDetails` of type `OfflineTransactionDetails | null`.
    if(version < 2) version = 2;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
